/* @jsxImportSource @emotion/react */

import React, { useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Modal } from '@slite/components'

import { LeadCaptureForm } from '../../../components/LeadGate/LeadCaptureForm'
import { useRoutes } from '../../../routes'
import { track } from '../../../utils/analytics'

/* Taller than a phone screen: capped to the viewport and scrolled inside. */
const bodyCss = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;

  @media screen and (max-width: 40rem) {
    padding: 16px;
    max-height: calc(100dvh - 16px);
  }
`

/* Not dismissible on purpose: the whole page is gated and the back link is
   the way out. Lead capture only, the content stays in the page HTML. */
export const WebinarGateModal = ({
  isOpen,
  slug,
  title,
  onUnlocked,
}: {
  isOpen: boolean
  slug: string
  title: string
  onUnlocked: () => void
}) => {
  const intl = useIntl()
  const routes = useRoutes()
  const firstNameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    track({ eventName: 'WEBSITE_WEBINAR_GATE_OPENED', eventData: { slug } })
    /* Autofocus would pop the keyboard on phones. */
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
    if (isTouchDevice) return
    const frame = requestAnimationFrame(() =>
      firstNameInputRef.current?.focus()
    )
    return () => cancelAnimationFrame(frame)
  }, [isOpen, slug])

  const unlock = () => {
    track({ eventName: 'WEBSITE_WEBINAR_GATE_UNLOCKED', eventData: { slug } })
    onUnlocked()
  }

  return (
    <Modal
      isOpen={isOpen}
      size={520}
      maxWidthEnabled={false}
      shouldCloseOnOverlayClick={false}
      className="background-moon-dust-lighter shadow-large"
      // Above the fixed navbar (zIndex 1000).
      overlayStyle={{ zIndex: 1100, overscrollBehavior: 'contain' }}
    >
      <div css={bodyCss}>
        <div>
          <h2 className="font-family-title font-size-3 font-weight-medium shade-05 margin-bottom-8">
            <FormattedMessage
              id="webinars.show.gate.title"
              defaultMessage="Watch {title}"
              values={{ title }}
            />
          </h2>
          <p className="font-size-6 shade-10 padding-bottom-24">
            <FormattedMessage
              id="webinars.show.gate.description"
              defaultMessage="Fill in your details and the recording and written recap unlock right here. No email to wait for."
            />
          </p>
        </div>
        <LeadCaptureForm
          source="webinar-gate"
          contentSlug={slug}
          submitLabel={intl.formatMessage({
            id: 'webinars.show.gate.submit',
            defaultMessage: 'Watch the webinar',
          })}
          onSuccess={unlock}
          firstNameInputRef={firstNameInputRef}
        />
        <a
          href={routes.webinars.index()}
          className="font-size-7 shade-20 shade-05-on-hover text-align-center underline"
        >
          <FormattedMessage
            id="webinars.show.gate.back"
            defaultMessage="Back to all webinars"
          />
        </a>
      </div>
    </Modal>
  )
}
